import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, ErrorNote, Field } from '../components/ui';
import { api, ApiError, authClient, type Me } from '../lib/api';
import { useAuthError } from '../lib/auth-errors';
import { useLocale, useT } from '../lib/i18n';
import { AuthShell } from './auth';

type Invitation = { salonName: string; staffName: string | null; email: string | null; role: 'owner' | 'manager' | 'staff' };

/** /uitnodiging?token=…: join a salon as a team member (sign up or log in with the invited address). */
export function InvitationPage() {
  const t = useT();
  const authError = useAuthError();
  const locale = useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { token = '' } = useSearch({ from: '/uitnodiging' });
  const invite = useQuery({ queryKey: ['invitation', token], queryFn: () => api<{ invitation: Invitation }>(`/invitations/${encodeURIComponent(token)}`).then((r) => r.invitation), retry: false });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me').catch(() => null), retry: false });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const accept = useMutation({
    mutationFn: () => api<{ orgId: string }>(`/invitations/${encodeURIComponent(token)}/accept`, { method: 'POST' }),
    onSuccess: ({ orgId }) => {
      qc.clear();
      navigate({ to: '/s/$orgId/vandaag', params: { orgId } });
    },
  });

  async function signUp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    const { error: err } = await authClient.signUp.email({ name: String(form.get('name')), email: invite.data!.email!, password: String(form.get('password')), locale } as Parameters<typeof authClient.signUp.email>[0]);
    setBusy(false);
    if (err) return setError(authError(err));
    qc.removeQueries({ queryKey: ['me'] });
    accept.mutate();
  }

  if (invite.isPending || me.isPending) return <AuthShell title={t('common.loading')}>{null}</AuthShell>;
  if (!invite.data) {
    return (
      <AuthShell title={t('invite.invalidTitle')} subtitle={invite.error instanceof ApiError ? invite.error.message : t('error.generic')}>
        <Link to="/inloggen" className="font-semibold text-accent-strong hover:underline">
          {t('auth.login.submit')}
        </Link>
      </AuthShell>
    );
  }
  const i = invite.data;
  const title = t('invite.title', { salon: i.salonName });
  const subtitle = i.staffName ? t('invite.subtitle', { name: i.staffName }) : undefined;

  // Logged in already: accept with one tap (the server checks the email address).
  if (me.data) {
    const wrong = i.email && me.data.user.email.toLowerCase() !== i.email.toLowerCase();
    return (
      <AuthShell title={title} subtitle={subtitle}>
        <div className="flex flex-col gap-4">
          {wrong ? (
            <>
              <p>{t('invite.otherAccount', { email: i.email ?? '', current: me.data.user.email })}</p>
              <Button
                variant="secondary"
                onClick={async () => {
                  await authClient.signOut();
                  qc.clear();
                  void me.refetch();
                }}
              >
                {t('account.logout')}
              </Button>
            </>
          ) : (
            <Button onClick={() => accept.mutate()} disabled={accept.isPending}>
              {t('invite.accept')}
            </Button>
          )}
          <ErrorNote message={accept.error instanceof ApiError ? accept.error.message : accept.error ? t('error.generic') : null} />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={title} subtitle={subtitle}>
      <form onSubmit={signUp} className="flex flex-col gap-4">
        <Field id="email" type="email" label={t('field.email')} value={i.email ?? ''} readOnly />
        <Field id="name" name="name" autoComplete="name" required label={t('field.name')} defaultValue={i.staffName ?? ''} />
        <Field id="password" name="password" type="password" autoComplete="new-password" minLength={10} required label={t('field.password')} hint={t('field.passwordHint')} />
        <ErrorNote message={error ?? (accept.error instanceof ApiError ? accept.error.message : null)} />
        <Button type="submit" disabled={busy || accept.isPending}>
          {t('invite.signUp')}
        </Button>
      </form>
      <p className="mt-6 text-sm text-muted">
        {t('auth.signup.haveAccount')}{' '}
        <Link to="/inloggen" search={{ uitnodiging: token }} className="font-semibold text-accent-strong underline-offset-2 hover:underline">
          {t('auth.login.submit')}
        </Link>
      </p>
    </AuthShell>
  );
}

/** Wachtwoord vergeten: always the same answer, so nobody learns which addresses have an account. */
export function ForgotPasswordPage() {
  const t = useT();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    await authClient.requestPasswordReset({ email: String(new FormData(e.currentTarget).get('email')), redirectTo: `${window.location.origin}/wachtwoord-nieuw` });
    setBusy(false);
    setSent(true);
  }
  return (
    <AuthShell title={t('auth.forgotTitle')} subtitle={sent ? undefined : t('auth.forgotIntro')}>
      {sent ? (
        <p aria-live="polite">{t('auth.forgotSent')}</p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field id="email" name="email" type="email" autoComplete="email" required label={t('field.email')} />
          <Button type="submit" disabled={busy}>
            {t('auth.forgotSend')}
          </Button>
        </form>
      )}
      <p className="mt-6 text-sm">
        <Link to="/inloggen" className="font-semibold text-accent-strong hover:underline">
          ← {t('auth.login.title')}
        </Link>
      </p>
    </AuthShell>
  );
}

/** The link in the reset email lands here with ?token=… */
export function ResetPasswordPage() {
  const t = useT();
  const authError = useAuthError();
  const navigate = useNavigate();
  const { token, error: linkError } = useSearch({ from: '/wachtwoord-nieuw' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    const { error: err } = await authClient.resetPassword({ newPassword: String(new FormData(e.currentTarget).get('password')), token });
    setBusy(false);
    if (err) return setError(authError(err));
    navigate({ to: '/inloggen' });
  }
  if (!token || linkError) {
    return (
      <AuthShell title={t('auth.resetInvalid')}>
        <Link to="/wachtwoord-vergeten" className="font-semibold text-accent-strong hover:underline">
          {t('auth.forgotSend')}
        </Link>
      </AuthShell>
    );
  }
  return (
    <AuthShell title={t('auth.resetTitle')}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field id="password" name="password" type="password" autoComplete="new-password" minLength={10} required label={t('auth.newPassword')} hint={t('field.passwordHint')} />
        <ErrorNote message={error} />
        <Button type="submit" disabled={busy}>
          {t('common.save')}
        </Button>
      </form>
    </AuthShell>
  );
}

/** Shown in the app until the owner has confirmed their email address. */
export function VerifyEmailBanner({ email }: { email: string }) {
  const t = useT();
  const [sent, setSent] = useState(false);
  return (
    <div className="mb-3 flex items-center justify-between gap-3 rounded-full bg-warning-soft px-4 py-2 text-[13px] sm:mb-4 sm:rounded-[var(--radius-control)] sm:py-2.5 sm:text-sm">
      {/* Phones: one short line, so it doesn't push the page down. */}
      <span className="sm:hidden">{t('verify.bannerShort')}</span>
      <span className="hidden sm:inline">{t('verify.banner', { email })}</span>
      <button
        type="button"
        className="shrink-0 font-semibold text-warning underline-offset-2 hover:underline disabled:opacity-60"
        disabled={sent}
        onClick={async () => {
          await authClient.sendVerificationEmail({ email, callbackURL: '/' });
          setSent(true);
        }}
      >
        {sent ? `✓ ${t('verify.sent')}` : t('verify.resend')}
      </button>
    </div>
  );
}
